/**
 * Algolia Direct Client — bypasses Vercel proxy for faster responses (~400ms saved).
 *
 * Uses the public search-only API key (already exposed in window.__ALGOLIA_FALLBACK_CONFIG__).
 * The API surface (window.AlgoliaProxy.*) is unchanged so all consuming scripts work as-is.
 *
 * Public API:
 *   window.AlgoliaProxy.searchClient()        → { initIndex, search, addAlgoliaAgent }
 *   window.AlgoliaProxy.recommendClient()     → { getRecommendations }
 *   window.AlgoliaProxy.search(requests)
 *   window.AlgoliaProxy.clientSearch(requests)
 *   window.AlgoliaProxy.indexSearch(name, query, opts)
 *   window.AlgoliaProxy.getCircuitState()     → { failures: 0, openUntil: 0, isOpen: false }
 */

(function () {
  'use strict';

  function getConfig() {
    const cfg = window.__ALGOLIA_FALLBACK_CONFIG__;
    if (!cfg || !cfg.appId || !cfg.apiKey) {
      throw new Error('Algolia config missing (window.__ALGOLIA_FALLBACK_CONFIG__)');
    }
    return cfg;
  }

  function paramsToQueryString(params) {
    if (!params) return '';
    return Object.entries(params)
      .map(function (entry) {
        var k = entry[0], v = entry[1];
        if (v === undefined || v === null) return null;
        var value = typeof v === 'object' ? JSON.stringify(v) : String(v);
        return encodeURIComponent(k) + '=' + encodeURIComponent(value);
      })
      .filter(Boolean)
      .join('&');
  }

  function algoliaBaseUrl() {
    return 'https://' + getConfig().appId.toLowerCase() + '-dsn.algolia.net';
  }

  function algoliaHeaders() {
    var cfg = getConfig();
    return {
      'Content-Type': 'application/json',
      'X-Algolia-Application-Id': cfg.appId,
      'X-Algolia-API-Key': cfg.apiKey,
    };
  }

  async function multiSearch(requests) {
    var body = {
      requests: requests.map(function (req) {
        var params = Object.assign({}, req.params || {});
        if (req.query !== undefined) params.query = req.query;
        return {
          indexName: req.indexName,
          params: paramsToQueryString(params),
        };
      }),
    };
    var response = await fetch(algoliaBaseUrl() + '/1/indexes/*/queries', {
      method: 'POST',
      headers: algoliaHeaders(),
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw new Error('Algolia HTTP ' + response.status);
    }
    return await response.json();
  }

  // Récupération d'un record par objectID (endpoint objects, PAS un search request
  // facturé). params: objet simple, ex. { attributesToRetrieve: 'collections,title' }.
  async function getObject(indexName, objectID, params) {
    var qs = params ? '?' + paramsToQueryString(params) : '';
    var response = await fetch(
      algoliaBaseUrl() + '/1/indexes/' + encodeURIComponent(indexName) + '/' + encodeURIComponent(objectID) + qs,
      { headers: algoliaHeaders() }
    );
    if (!response.ok) {
      throw new Error('Algolia getObject HTTP ' + response.status);
    }
    return await response.json();
  }

  async function recommend(requests) {
    // Proxy Vercel pour recommend (la clé search-only n'a pas l'ACL recommendation)
    var response = await fetch('https://oblist-algolia-proxy.vercel.app/recommend', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ requests: requests }),
    });
    if (!response.ok) {
      throw new Error('Algolia Recommend HTTP ' + response.status);
    }
    return await response.json();
  }

  // --- Public API (unchanged shape) ---

  function searchClient() {
    return {
      initIndex: function (indexName) {
        return {
          search: async function (query, options) {
            var data = await multiSearch([
              { indexName: indexName, query: query || '', params: options || {} }
            ]);
            var result = data.results && data.results.length > 0 ? data.results[0] : data;
            return Object.assign({
              hits: result.hits || [],
              queryID: result.queryID || null,
              nbHits: result.nbHits || 0,
              nbPages: result.nbPages || 0,
              page: result.page || 0,
              processingTimeMS: result.processingTimeMS || 0,
            }, result);
          }
        };
      },

      search: async function (requests) {
        if (!Array.isArray(requests)) {
          throw new Error('client.search() expects an array of search requests');
        }
        var data = await multiSearch(requests);
        return Object.assign({
          results: data.results || requests.map(function () {
            return { hits: [], queryID: null, nbHits: 0, nbPages: 0, page: 0, processingTimeMS: 0 };
          }),
        }, data);
      },

      addAlgoliaAgent: function () {}
    };
  }

  function recommendClient() {
    return {
      getRecommendations: async function (options) {
        if (!options || !options.requests || !Array.isArray(options.requests)) {
          throw new Error('getRecommendations() expects an object with a requests array');
        }
        var data = await recommend(options.requests);
        return Object.assign({
          results: data.results || options.requests.map(function () {
            return { hits: [], queryID: null };
          }),
        }, data);
      }
    };
  }

  window.AlgoliaProxy = {
    searchClient: searchClient,
    recommendClient: recommendClient,
    getObject: getObject,
    search: async function (requests) {
      return await searchClient().search(requests);
    },
    clientSearch: async function (requests) {
      return await searchClient().search(requests);
    },
    indexSearch: async function (indexName, query, options) {
      return await searchClient().initIndex(indexName).search(query, options);
    },
    getCircuitState: function () {
      return { failures: 0, openUntil: 0, isOpen: false };
    },
  };
})();
